import { useAuth0 } from '@auth0/auth0-react';
import { User } from '@remarkable/ark-icons';
import { RadioButtonField, Typography } from '@remarkable/ark-web';
import { invariant, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import type { EnterpriseOffboardingType } from 'src/api/endpoints/cloudEnterpriseAdminApi.types';
import {
  useCompleteOffboarding,
  useEnterpriseOrgSettings,
} from 'src/api/queries/enterpriseAdmin';
import { Button, NotificationBox, Sheet, Spinner } from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { IS_DEVELOPMENT } from 'src/config';
import { TestId } from 'src/testids';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';
import { userHelpers } from 'src/utils/userHelpers';

export function OffboardingPage() {
  const auth = useAuth0();

  invariant(auth.user, 'User must be logged in to access this route.');

  const organization =
    userHelpers.getOrganizationDisplayName(auth.user) || 'your organization';

  return (
    <AppPage.Container
      data-component-location="Enterprise Offboarding Page"
      data-cy={TestId.PageEnterpriseOffboarding}
    >
      <AppPage.Header
        title={`Offboarding ${organization}`}
        className="max-w-l2xl text-center"
      />
      <AppPage.Content className="max-w-ll">
        <OffboardingPageContent />
      </AppPage.Content>
    </AppPage.Container>
  );
}

function OffboardingPageContent() {
  const orgSettings = useEnterpriseOrgSettings();
  const [offboardingType, setOffboardingType] =
    useState<EnterpriseOffboardingType>('DELETE');
  const completeOffboarding = useCompleteOffboarding();
  const auth = useAuth0();
  const featureFlags = useFeatureFlags();

  if (!auth.user) {
    const error = new Error('Missing user information.');
    return (
      <NotificationBox error={error}>Missing user information.</NotificationBox>
    );
  }

  switch (orgSettings.data?.offboardingStatus) {
    case 'IDLE':
    case 'OFFBOARDABLE':
      return <Navigate to="/enterprise/billing" replace={true} />;

    case 'DEACTIVING_USERS':
      return (
        <div className="flex flex-col items-center justify-center gap-24">
          <Typography variant="interface-lg" className="mt-4">
            Offboarding in progress: Deactivating users...
          </Typography>

          <Spinner />
        </div>
      );
    case 'EXPORTING_DATA':
      return (
        <div className="flex flex-col items-center justify-center gap-24">
          <Typography variant="interface-lg" className="mt-4">
            Offboarding in progress: Exporting data...
          </Typography>

          <Spinner />
        </div>
      );

    case 'USERS_DEACTIVATED':
    case 'DATA_EXPORTED':
      return (
        <Sheet data-component-location="Complete offboarding sheet">
          <Typography variant="heading-sm" className="mb-8">
            Ready to complete offboarding
          </Typography>

          <Typography variant="body-md" className="mb-16">
            All associated user accounts have been deactivated.
          </Typography>

          {featureFlags.EnableOffboardingTypeSelection && (
            <>
              <Typography variant="heading-sm">
                <User className="inline align-top" /> User management
              </Typography>

              <RadioButtonField
                name="offboarding-strategy"
                label="Delete all user accounts, incl. user data"
                checked={offboardingType === 'DELETE'}
                onChange={(e) => {
                  if (e.target.checked) {
                    setOffboardingType('DELETE');
                  }
                }}
              />
              <RadioButtonField
                name="offboarding-strategy"
                label="Migrate all user accounts to private accounts, incl. user data"
                checked={offboardingType === 'MIGRATE'}
                onChange={(e) => {
                  if (e.target.checked) {
                    setOffboardingType('MIGRATE');
                  }
                }}
              />
            </>
          )}

          <div className="flex justify-center">
            <Button
              variant="primary"
              className="mt-16"
              mobileFullWidth
              onClick={() => {
                completeOffboarding.mutate({ offboardingType });
              }}
              disabled={completeOffboarding.isPending}
              loading={completeOffboarding.isPending}
            >
              Complete offboarding
            </Button>
          </div>

          <NotificationBox
            error={completeOffboarding.error}
            className="mt-16"
          />
        </Sheet>
      );

    case 'FINALIZING':
    case 'OFFBOARDED':
      return (
        <Sheet>
          <Typography variant="heading-md" className="mb-8">
            Offboarding Completed
          </Typography>

          <Typography variant="body-md" className="mb-16">
            Your organization has been successfully offboarded. Permanent
            deletion of all organization data is scheduled within 90 days.
          </Typography>

          {/* TODO: Add this back once we actually do it. */}
          {/* <Typography variant="body-md" className="mb-8">
            Once the data has been deleted, we&apos;ll send a confirmation email
            to: <br />
            <b>{userHelpers.getEmail(auth.user)}</b>
          </Typography> */}

          <div className="flex justify-center">
            <Button as="a" variant="primary" to="/logout" mobileFullWidth>
              Log Out
            </Button>
          </div>
        </Sheet>
      );

    default:
      return (
        <div className="flex flex-col items-center justify-center gap-24">
          <Typography variant="interface-lg" className="mb-8">
            Unable to determine offboarding status.{' '}
          </Typography>

          {IS_DEVELOPMENT && (
            <NotificationBox
              variant="info"
              className="mb-4 w-full"
              title="Status"
            >
              &quot;{orgSettings.data?.offboardingStatus}&quot;
            </NotificationBox>
          )}

          <Button variant="primary" onClick={() => orgSettings.refetch()}>
            Retry
          </Button>
        </div>
      );
  }
}
